import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { FileText, Loader2, Download, Lightbulb } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const DOC_TYPES = [
  "Drawing",
  "BOM",
  "Purchase Order",
  "Quotation",
  "Invoice",
  "Mezzanine",
  "Design",
  "Client Docs",
  "Others",
];

// Heuristic: suggest doc type from filename
function suggestDocType(filename = "") {
  const f = filename.toLowerCase();
  if (/(^|[\W_])bom([\W_]|$)/.test(f)) return "BOM";
  if (/(invoice|inv[\W_]|bill)/.test(f)) return "Invoice";
  if (/(po[\W_]|purchase[\W_]?order|p\.?o\.?[\W_])/.test(f)) return "Purchase Order";
  if (/(quote|quotation|qtn)/.test(f)) return "Quotation";
  if (/(drawing|dwg|draft|isometric|gad)/.test(f)) return "Drawing";
  if (/(design|render|layout)/.test(f)) return "Design";
  if (/(mezz)/.test(f)) return "Mezzanine";
  return null;
}

/**
 * ClassifyAttachmentDialog
 * Props:
 *  - open
 *  - onClose()
 *  - target: { email_id, attachment_index, filename, content_type, project_id, size }
 *  - onClassified()  - callback after success
 */
export default function ClassifyAttachmentDialog({ open, onClose, target, onClassified }) {
  const [docType, setDocType] = useState("");
  const [mode, setMode] = useState("new_version");
  const [versionLabel, setVersionLabel] = useState("v1");
  const [existingVersionGroupId, setExistingVersionGroupId] = useState("");
  const [replaceFileId, setReplaceFileId] = useState("");
  const [notes, setNotes] = useState("");
  const [versionGroups, setVersionGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [suggestedType, setSuggestedType] = useState(null);

  useEffect(() => {
    if (!open || !target) return;
    // reset
    const suggested = suggestDocType(target.filename);
    setSuggestedType(suggested);
    setDocType(suggested || "");
    setMode("new_version");
    setVersionLabel("v1");
    setExistingVersionGroupId("");
    setReplaceFileId("");
    setNotes("");
    fetchVersionGroups();
    fetchPreview();
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, target?.email_id, target?.attachment_index]);

  const fetchVersionGroups = async () => {
    if (!target?.project_id) return;
    try {
      const res = await axios.get(`${API}/projects/${target.project_id}/versions`, auth());
      setVersionGroups(res.data || []);
    } catch (e) {
      // Some installs use /documents/projects/{id}/versions; try both
      try {
        const res = await axios.get(`${API}/documents/projects/${target.project_id}/versions`, auth());
        setVersionGroups(res.data || []);
      } catch {
        setVersionGroups([]);
      }
    }
  };

  const fetchPreview = async () => {
    if (!target) return;
    setPreviewLoading(true);
    try {
      const res = await axios.get(
        `${API}/email/attachments/${target.email_id}/${target.attachment_index}/download`,
        { ...auth(), responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      setPreviewUrl(url);
    } catch (e) {
      // No preview available
      setPreviewUrl(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const filteredVersionGroups = versionGroups.filter(
    (vg) => !docType || vg.doc_type === docType
  );

  const handleSubmit = async () => {
    if (!docType) {
      toast.error("Select a document type");
      return;
    }
    if (mode !== "new_version" && !existingVersionGroupId) {
      toast.error("Select a version group");
      return;
    }
    if (mode === "replace_in_existing" && !replaceFileId) {
      toast.error("Select a file to replace");
      return;
    }

    setLoading(true);
    try {
      const body = {
        doc_type: docType,
        mode,
        version_label: mode === "new_version" ? versionLabel : null,
        existing_version_group_id: mode !== "new_version" ? existingVersionGroupId : null,
        replace_file_id: mode === "replace_in_existing" ? replaceFileId : null,
        notes: notes || null,
      };
      await axios.post(
        `${API}/email/attachments/${target.email_id}/${target.attachment_index}/classify`,
        body,
        auth()
      );
      toast.success(`Classified as ${docType}`);
      onClassified?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to classify");
    } finally {
      setLoading(false);
    }
  };

  const isImage = target?.content_type?.startsWith("image/");
  const isPdf = target?.content_type === "application/pdf";
  const selectedVG = versionGroups.find((vg) => vg.id === existingVersionGroupId);

  // Don't render dialog body when target is missing — Dialog still mounts when closed
  if (!target) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-[95vw] w-[95vw] max-h-[95vh] overflow-y-auto" data-testid="classify-attachment-dialog">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Classify Attachment
              </DialogTitle>
              <p className="text-sm text-gray-500 mt-1 break-all">{target?.filename}</p>
            </div>
            {previewUrl && (
              <a
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 rounded"
                data-testid="open-preview-newtab-btn"
              >
                <Download className="w-3 h-3" /> Open in new tab
              </a>
            )}
          </div>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mt-4">
          {/* Preview - takes 3/5 of width on lg+ */}
          <div className="lg:col-span-3 border border-gray-200 rounded-lg bg-gray-50 flex items-stretch justify-center overflow-auto" style={{ height: "75vh" }}>
            {previewLoading ? (
              <div className="text-gray-400 flex items-center gap-2 m-auto">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading preview…
              </div>
            ) : previewUrl ? (
              isImage ? (
                <div className="overflow-auto w-full h-full flex items-center justify-center p-2">
                  <img src={previewUrl} alt={target.filename} className="max-w-full object-contain" />
                </div>
              ) : isPdf ? (
                <iframe
                  src={`${previewUrl}#view=FitH`}
                  title="Preview"
                  className="w-full h-full border-0"
                />
              ) : (
                <div className="p-6 text-center m-auto">
                  <FileText className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                  <p className="text-sm text-gray-600 mb-3">{target.content_type || "Unknown type"}</p>
                  <a
                    href={previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={target.filename}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                  >
                    <Download className="w-4 h-4" /> Open / Download
                  </a>
                </div>
              )
            ) : (
              <div className="text-gray-400 text-sm">No preview available</div>
            )}
          </div>

          {/* Form */}
          <div className="lg:col-span-2 space-y-4 overflow-y-auto" style={{ maxHeight: "75vh" }}>
            <div>
              <Label className="text-sm font-medium">Document Type</Label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger className="mt-1" data-testid="classify-doc-type-select">
                  <SelectValue placeholder="Select document type" />
                </SelectTrigger>
                <SelectContent>
                  {DOC_TYPES.map((dt) => (
                    <SelectItem key={dt} value={dt}>{dt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {suggestedType && docType === suggestedType && (
                <div className="mt-1 flex items-center gap-1 text-xs text-amber-700" data-testid="classify-suggested-hint">
                  <Lightbulb className="w-3 h-3" /> Suggested from filename
                </div>
              )}
            </div>

            <div>
              <Label className="text-sm font-medium">How to add</Label>
              <RadioGroup value={mode} onValueChange={setMode} className="mt-2 space-y-2">
                <label className="flex items-start gap-2 cursor-pointer">
                  <RadioGroupItem value="new_version" data-testid="classify-mode-new" />
                  <div>
                    <div className="text-sm font-medium">Create new version group</div>
                    <div className="text-xs text-gray-500">Adds as a fresh version (no impact on existing).</div>
                  </div>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <RadioGroupItem value="add_to_existing" data-testid="classify-mode-add" />
                  <div>
                    <div className="text-sm font-medium">Add to existing version</div>
                    <div className="text-xs text-gray-500">New file inside an existing version group.</div>
                  </div>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <RadioGroupItem value="replace_in_existing" data-testid="classify-mode-replace" />
                  <div>
                    <div className="text-sm font-medium">Replace file in existing version</div>
                    <div className="text-xs text-gray-500">Supersedes a specific file inside that version.</div>
                  </div>
                </label>
              </RadioGroup>
            </div>

            {mode === "new_version" && (
              <div>
                <Label className="text-sm font-medium">Version Label</Label>
                <Input
                  value={versionLabel}
                  onChange={(e) => setVersionLabel(e.target.value)}
                  placeholder="v1"
                  className="mt-1"
                  data-testid="classify-version-label-input"
                />
              </div>
            )}

            {mode !== "new_version" && (
              <div>
                <Label className="text-sm font-medium">Existing Version Group</Label>
                <Select value={existingVersionGroupId} onValueChange={setExistingVersionGroupId}>
                  <SelectTrigger className="mt-1" data-testid="classify-existing-vg-select">
                    <SelectValue placeholder={docType ? "Pick a version group" : "Select a doc type first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredVersionGroups.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-gray-500">No matching version groups</div>
                    ) : (
                      filteredVersionGroups.map((vg) => (
                        <SelectItem key={vg.id} value={vg.id}>
                          {vg.doc_type} · {vg.version} · {vg.status}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            {mode === "replace_in_existing" && selectedVG && (
              <div>
                <Label className="text-sm font-medium">File to Replace</Label>
                <Select value={replaceFileId} onValueChange={setReplaceFileId}>
                  <SelectTrigger className="mt-1" data-testid="classify-replace-file-select">
                    <SelectValue placeholder="Pick file to supersede" />
                  </SelectTrigger>
                  <SelectContent>
                    {(selectedVG.files || []).filter(f => f.is_active).map((f) => (
                      <SelectItem key={f.id} value={f.id}>
                        rev {f.revision_number} · {f.file_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label className="text-sm font-medium">Notes (optional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="mt-1"
                data-testid="classify-notes-input"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="mt-6">
          <Button variant="ghost" onClick={onClose} disabled={loading} data-testid="classify-cancel-btn">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading || !docType} data-testid="classify-submit-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Classify as {docType || "…"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
